import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSelector, useDispatch } from 'react-redux';
import axiosInstance from '../../utils/axiosInstanse';
import Spiner from '../Spiner/Spiner';
import { showNotification } from '../../utils/notificationUtils';
import host from "../../host";
import './BlacklistEditor.css';

const BlacklistEditor = ({ isOpen, onClose }) => {
  const [blacklist, setBlacklist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [newEntry, setNewEntry] = useState({ fio: '', description: '' });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Состояния для модального окна подтверждения удаления
  const [deleteConfirm, setDeleteConfirm] = useState({
    isOpen: false,
    entryId: null,
    entryFio: ''
  });

  const dispatch = useDispatch();
  const notification = useSelector(state => state.notification);

  const fetchBlacklist = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`${host}api/get_black_list`);
      setBlacklist(response.data);
    } catch (err) {
      showNotification(
        dispatch,
        err.response?.data?.message || 'Не удалось загрузить черный список',
        'error'
      );
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    if (isOpen) {
      fetchBlacklist();
    }
  }, [isOpen, fetchBlacklist]);

  const validateForm = (values) => {
    const newErrors = {};
    if (!values.fio?.trim()) {
      newErrors.fio = 'ФИО обязательно для заполнения';
    } else if (values.fio.length > 200) {
      newErrors.fio = 'ФИО не должно превышать 200 символов';
    }

    if (values.description && values.description.length > 500) {
      newErrors.description = 'Описание не должно превышать 500 символов';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddEntry = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!validateForm(newEntry)) {
      showNotification(dispatch, 'Пожалуйста, исправьте ошибки в форме', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axiosInstance.post(`${host}api/black_list/`, newEntry);

      if (response.status === 200 || response.status === 201) {
        // Добавляем новую запись локально — без re-fetch
        const addedEntry = response.data || { id: Date.now(), ...newEntry };
        setBlacklist(prev => [addedEntry, ...prev]);
        showNotification(dispatch, 'Запись успешно добавлена', 'success');
        setNewEntry({ fio: '', description: '' });
        setIsAdding(false);
        setErrors({});
      }
    } catch (err) {
      showNotification(
        dispatch,
        err.response?.data?.message || 'Ошибка при добавлении записи',
        'error'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateEntry = async (id, updatedData) => {
    setSubmitting(true);
    try {
      const response = await axiosInstance.put(`${host}api/black_list/${id}/`, updatedData);

      if (response.status === 200) {
        // Обновляем запись локально — без re-fetch
        setBlacklist(prev =>
          prev.map(item =>
            item.id === id
              ? { ...item, ...updatedData }
              : item
          )
        );
        showNotification(dispatch, 'Запись успешно обновлена', 'success');
        setEditingEntry(null);
      }
    } catch (err) {
      showNotification(
        dispatch,
        err.response?.data?.message || 'Ошибка при обновлении записи',
        'error'
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Открыть модальное окно подтверждения удаления
  const openDeleteConfirm = (id, fio) => {
    setDeleteConfirm({
      isOpen: true,
      entryId: id,
      entryFio: fio
    });
  };

  // Закрыть модальное окно подтверждения удаления
  const closeDeleteConfirm = () => {
    setDeleteConfirm({
      isOpen: false,
      entryId: null,
      entryFio: ''
    });
  };

  // Подтверждение удаления
  const confirmDelete = async () => {
    const { entryId } = deleteConfirm;
    closeDeleteConfirm();

    try {
      const response = await axiosInstance.delete(`${host}api/black_list/${entryId}/`);

      if (response.status === 200 || response.status === 204) {
        // Удаляем запись локально — без re-fetch
        setBlacklist(prev => prev.filter(item => item.id !== entryId));
        showNotification(dispatch, 'Запись успешно удалена', 'success');
      }
    } catch (err) {
      showNotification(
        dispatch,
        err.response?.data?.message || 'Ошибка при удалении записи',
        'error'
      );
    }
  };

  const handleEditClick = (entry) => {
    setEditingEntry({
      id: entry.id,
      fio: entry.fio,
      description: entry.description || ''
    });
  };

  const handleEditCancel = () => {
    setEditingEntry(null);
  };

  const handleEditSave = () => {
    if (!editingEntry) return;

    if (!editingEntry.fio?.trim()) {
      showNotification(dispatch, 'ФИО обязательно для заполнения', 'error');
      return;
    }

    handleUpdateEntry(editingEntry.id, {
      fio: editingEntry.fio,
      description: editingEntry.description
    });
  };

  if (!isOpen) return null;

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.2 } },
    exit: { opacity: 0, transition: { duration: 0.15 } }
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.95, y: 20 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: { type: "spring", damping: 28, stiffness: 300 }
    },
    exit: { opacity: 0, scale: 0.95, y: 20, transition: { duration: 0.15 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 8 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.2 } },
    exit: { opacity: 0, x: -40, transition: { duration: 0.15 } }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="ble-overlay"
          onClick={onClose}
          variants={overlayVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          <motion.div
            className="ble-modal"
            onClick={(e) => e.stopPropagation()}
            variants={modalVariants}
          >
            <div className="ble-header">
              <h2>🚫 Редактор черного списка</h2>
              <button className="ble-close-btn" onClick={onClose}>✕</button>
            </div>

            <div className="ble-body">
              {/* Кнопка добавления */}
              <motion.button
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                className={`ble-toggle-btn ${isAdding ? 'ble-toggle-btn--cancel' : 'ble-toggle-btn--add'}`}
                onClick={() => {
                  setIsAdding(!isAdding);
                  setEditingEntry(null);
                  setErrors({});
                  setNewEntry({ fio: '', description: '' });
                }}
              >
                {isAdding ? '✕ Отмена' : '+ Добавить запись'}
              </motion.button>

              {/* Форма добавления — без AnimatePresence height animation (причина рывков) */}
              {isAdding && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="ble-add-form">
                    <form onSubmit={handleAddEntry}>
                      <div className="ble-field">
                        <label>ФИО <span className="required">*</span></label>
                        <input
                          type="text"
                          className={`ble-input ${errors.fio ? 'ble-input--error' : ''}`}
                          value={newEntry.fio}
                          onChange={(e) => {
                            setNewEntry({ ...newEntry, fio: e.target.value });
                            if (errors.fio) setErrors({ ...errors, fio: null });
                          }}
                          placeholder="Введите ФИО"
                          maxLength={200}
                        />
                        {errors.fio && <div className="ble-error-text">{errors.fio}</div>}
                      </div>

                      <div className="ble-field">
                        <label>Причина / Описание</label>
                        <textarea
                          className={`ble-input ${errors.description ? 'ble-input--error' : ''}`}
                          rows={3}
                          value={newEntry.description}
                          onChange={(e) => {
                            setNewEntry({ ...newEntry, description: e.target.value });
                            if (errors.description) setErrors({ ...errors, description: null });
                          }}
                          placeholder="Введите причину добавления в черный список"
                          maxLength={500}
                          style={{ resize: 'vertical' }}
                        />
                        {errors.description && <div className="ble-error-text">{errors.description}</div>}
                        <div className="ble-char-count">{newEntry.description.length}/500 символов</div>
                      </div>

                      <motion.button
                        whileHover={!submitting ? { scale: 1.01 } : {}}
                        whileTap={!submitting ? { scale: 0.99 } : {}}
                        type="submit"
                        className="ble-submit-btn"
                        disabled={submitting}
                      >
                        {submitting ? (
                          <>
                            <span className="ble-spinner" />
                            Сохранение...
                          </>
                        ) : (
                          '💾 Сохранить'
                        )}
                      </motion.button>
                    </form>
                  </div>
                </motion.div>
              )}

              {/* Список записей */}
              {loading ? (
                <div className="ble-loading">
                  <Spiner />
                </div>
              ) : blacklist.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="ble-empty"
                >
                  📭 Черный список пуст
                </motion.div>
              ) : (
                <div className="ble-list">
                  <AnimatePresence>
                    {blacklist.map(entry => (
                      <motion.div
                        key={entry.id}
                        variants={itemVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        layout
                        className="ble-item"
                      >
                        {editingEntry && editingEntry.id === entry.id ? (
                          // Режим редактирования
                          <div className="ble-edit-mode">
                            <div className="ble-field">
                              <label>ФИО <span className="required">*</span></label>
                              <input
                                type="text"
                                className="ble-input"
                                value={editingEntry.fio}
                                onChange={(e) => setEditingEntry({
                                  ...editingEntry,
                                  fio: e.target.value
                                })}
                                placeholder="Введите ФИО"
                              />
                            </div>

                            <div className="ble-field">
                              <label>Причина / Описание</label>
                              <textarea
                                className="ble-input"
                                rows={2}
                                value={editingEntry.description}
                                onChange={(e) => setEditingEntry({
                                  ...editingEntry,
                                  description: e.target.value
                                })}
                                placeholder="Введите причину"
                                style={{ resize: 'vertical' }}
                              />
                            </div>

                            <div className="ble-edit-actions">
                              <motion.button
                                whileHover={!submitting ? { scale: 1.02 } : {}}
                                whileTap={!submitting ? { scale: 0.98 } : {}}
                                className="ble-save-btn"
                                onClick={handleEditSave}
                                disabled={submitting}
                              >
                                {submitting ? (
                                  <span className="ble-spinner" />
                                ) : '💾 Сохранить'}
                              </motion.button>
                              <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                className="ble-cancel-btn"
                                onClick={handleEditCancel}
                              >
                                Отмена
                              </motion.button>
                            </div>
                          </div>
                        ) : (
                          // Режим просмотра
                          <>
                            <div className="ble-item-header">
                              <span className="ble-item-fio">👤 {entry.fio}</span>
                              <div className="ble-item-actions">
                                <motion.button
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                  className="ble-icon-btn ble-icon-btn--edit"
                                  onClick={() => handleEditClick(entry)}
                                  title="Редактировать"
                                >
                                  ✏️
                                </motion.button>
                                <motion.button
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                  className="ble-icon-btn ble-icon-btn--delete"
                                  onClick={() => openDeleteConfirm(entry.id, entry.fio)}
                                  title="Удалить"
                                >
                                  🗑️
                                </motion.button>
                              </div>
                            </div>
                            {entry.description && (
                              <div className="ble-item-reason">
                                <strong>Причина:</strong> {entry.description}
                              </div>
                            )}
                          </>
                        )}
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              )}
            </div>
          </motion.div>

          {/* Модальное окно подтверждения удаления */}
          <AnimatePresence>
            {deleteConfirm.isOpen && (
              <motion.div
                className="ble-del-overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={closeDeleteConfirm}
              >
                <motion.div
                  className="ble-del-modal"
                  initial={{ scale: 0.9, opacity: 0, y: 20 }}
                  animate={{ scale: 1, opacity: 1, y: 0 }}
                  exit={{ scale: 0.9, opacity: 0, y: 20 }}
                  transition={{ type: "spring", damping: 25 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="ble-del-header">
                    <span style={{ fontSize: '1.5rem' }}>⚠️</span>
                    <h3>Подтверждение удаления</h3>
                  </div>

                  <div className="ble-del-body">
                    <p>Вы действительно хотите удалить запись:</p>
                    <div className="ble-del-fio">"{deleteConfirm.entryFio}"</div>
                    <p className="ble-del-warning">Это действие нельзя будет отменить.</p>
                  </div>

                  <div className="ble-del-actions">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="ble-del-btn-cancel"
                      onClick={closeDeleteConfirm}
                    >
                      Отмена
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="ble-del-btn-delete"
                      onClick={confirmDelete}
                    >
                      Удалить
                    </motion.button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BlacklistEditor;